import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Configuration automatique de l'API
// Utilise HTTPS en production pour éviter les problèmes de Mixed Content
const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:3001/api'
  : `${window.location.protocol}//${window.location.hostname}/api`;

export default function StatsEquipe() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentResults, setRecentResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Changer le titre de la page
    document.title = 'ESMANAGER - STATISTIQUES ÉQUIPE';
    
    loadTeamStats();
    loadRecentResults();
  }, []);

  const loadRecentResults = async () => {
    try {
      const response = await fetch(`${API_URL}/recent-results`);
      const data = await response.json();
      setRecentResults(data);
    } catch (error) {
      console.error('Erreur chargement derniers résultats:', error);
    }
  };

  const loadTeamStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/team-stats`);
      const data = await response.json();
      
      // S'assurer que toutes les compétitions sont présentes dans l'ordre : Championnat, Coupe, Amical
      const competitionsMapping = [
        { full: 'Championnat', short: 'CHAMP' },
        { full: 'Coupe', short: 'COUPE' },
        { full: 'Amical', short: 'AMIC' }
      ];
      const byTypeArray = Array.isArray(data.byType) ? data.byType : [];
      
      const byType = competitionsMapping.map(({ full, short }) => {
        const existing = byTypeArray.find(c => c.competition === full);
        return {
          competition: short,
          fullName: full,
          victoires: existing ? existing.victoires : 0,
          nuls: existing ? existing.nuls : 0,
          defaites: existing ? existing.defaites : 0,
          total_matchs: existing ? existing.total_matchs : 0
        };
      });
      
      setStats({
        global: data.global || { victoires: 0, nuls: 0, defaites: 0, total_matchs: 0 },
        byType: byType,
        globalAvg: data.globalAvg || [],
        avgByType: data.avgByType || []
      });
    } catch (error) {
      console.error('Erreur chargement stats équipe:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div>
        {/* Header avec bouton retour */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2, 
          bgcolor: '#fff',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <IconButton 
            onClick={() => navigate('/')} 
            sx={{ 
              p: 1,
              minWidth: 'auto',
              width: 'auto'
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <img src="/assets/logo_esma.png" alt="logo" style={{ height: '40px', marginLeft: 'auto', marginRight: 'auto' }} />
          <Box sx={{ width: '48px' }} />
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  if (!stats) {
    return (
      <div>
        {/* Header avec bouton retour */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2, 
          bgcolor: '#fff',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <IconButton 
            onClick={() => navigate('/')} 
            sx={{ 
              p: 1,
              minWidth: 'auto',
              width: 'auto'
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <img src="/assets/logo_esma.png" alt="logo" style={{ height: '40px', marginLeft: 'auto', marginRight: 'auto' }} />
          <Box sx={{ width: '48px' }} />
        </Box>
        
        <Typography variant="h6" sx={{ textAlign: 'center', mt: 4 }}>
          Aucune donnée disponible
        </Typography>
      </div>
    );
  }

  return (
    <div>
      {/* Header avec bouton retour */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 2, 
        bgcolor: '#fff',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <IconButton 
          onClick={() => navigate('/')} 
          sx={{ 
            p: 1,
            minWidth: 'auto',
            width: 'auto'
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <img src="/assets/logo_esma.png" alt="logo" style={{ height: '80px', marginLeft: 'auto', marginRight: 'auto' }} />
        <Box sx={{ width: '48px' }} />
      </Box>

      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 3 }}>
          Statistiques de l'équipe
        </Typography>

        {/* Statistiques globales */}
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ py: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5, textAlign: 'center' }}>
              Bilan Global
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Grid container spacing={1} sx={{ maxWidth: '400px' }}>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {stats.global.victoires}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      V
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {stats.global.nuls}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      N
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {stats.global.defaites}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      D
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {stats.global.total_matchs}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      Total
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>

        {/* 5 derniers résultats */}
        {recentResults.length > 0 && (
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: '0.7rem' }}>
              5 Derniers Résultats
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
              {recentResults.map((result) => {
                const isVictory = result.score_home > result.score_away;
                const isDraw = result.score_home === result.score_away;
                const isDefeat = result.score_home < result.score_away;
                
                return (
                  <Box
                    key={result.id}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.7
                      }
                    }}
                    onClick={() => navigate(`/match-detail/${result.id}`)}
                  >
                    {/* Badge V/N/D */}
                    <Box
                      sx={{
                        width: '35px',
                        height: '35px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isVictory ? '#4caf50' : isDraw ? '#ff9800' : '#f44336',
                        color: 'white',
                        fontSize: '0.85rem',
                        fontWeight: 'bold'
                      }}
                    >
                      {isVictory ? 'V' : isDraw ? 'N' : 'D'}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Moyennes globales - Accordion */}
        {stats.globalAvg && stats.globalAvg.length > 0 && (
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Moyennes par match (Global)
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {/* Statistiques Pour */}
                    <TableRow>
                      <TableCell colSpan={2} sx={{ backgroundColor: '#f5f5f5', py: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                          POUR
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {stats.globalAvg.filter(s => s.team === 'home').map((stat, index, arr) => (
                      <TableRow key={`home-${index}`}>
                        <TableCell sx={{ borderBottom: index === arr.length - 1 ? '2px solid #e0e0e0' : undefined }}>
                          <Typography variant="body2">
                            {stat.stat_label}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: index === arr.length - 1 ? '2px solid #e0e0e0' : undefined }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {stat.average.toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Statistiques Contre */}
                    <TableRow>
                      <TableCell colSpan={2} sx={{ backgroundColor: '#f5f5f5', py: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                          CONTRE
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {stats.globalAvg.filter(s => s.team === 'away').map((stat, index, arr) => (
                      <TableRow key={`away-${index}`}>
                        <TableCell sx={{ borderBottom: index === arr.length - 1 ? 'none' : undefined }}>
                          <Typography variant="body2">
                            {stat.stat_label}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: index === arr.length - 1 ? 'none' : undefined }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {stat.average.toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Statistiques par compétition - Accordions */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5, mt: 2 }}>
          Par Compétition
        </Typography>
        
        {stats.byType.map((comp, index) => {
          const compAvg = stats.avgByType ? stats.avgByType.filter(avg => avg.competition === comp.fullName) : [];
          
          return (
            <Accordion key={index} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between', pr: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {comp.competition}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      {comp.victoires}V {comp.nuls}N {comp.defaites}D
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {compAvg.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {/* Statistiques Pour */}
                        <TableRow>
                          <TableCell colSpan={2} sx={{ backgroundColor: '#f5f5f5', py: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                              POUR
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {compAvg.filter(s => s.team === 'home').map((stat, index, arr) => (
                          <TableRow key={`home-${index}`}>
                            <TableCell sx={{ borderBottom: index === arr.length - 1 ? '2px solid #e0e0e0' : undefined }}>
                              <Typography variant="body2">
                                {stat.stat_label}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ borderBottom: index === arr.length - 1 ? '2px solid #e0e0e0' : undefined }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {stat.average.toFixed(2)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                        
                        {/* Statistiques Contre */}
                        <TableRow>
                          <TableCell colSpan={2} sx={{ backgroundColor: '#f5f5f5', py: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                              CONTRE
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {compAvg.filter(s => s.team === 'away').map((stat, index, arr) => (
                          <TableRow key={`away-${index}`}>
                            <TableCell sx={{ borderBottom: index === arr.length - 1 ? 'none' : undefined }}>
                              <Typography variant="body2">
                                {stat.stat_label}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ borderBottom: index === arr.length - 1 ? 'none' : undefined }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {stat.average.toFixed(2)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Aucune statistique disponible
                    </Typography>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </div>
  );
}

